// Constantes + helpers PURS du diagnostic technique (console + réseau), sans
// dépendance au DOM : testables et importables partout — shell, app, et vues
// serveur de `apps/desk` qui doivent filtrer le blob par content-type.
// L'instrumentation elle-même vit dans `diagnostics-capture.ts`.
//
// Miroir volontaire de `rrweb.ts` (même séparation pur/DOM) : ce module ne
// connaît que la FORME de la donnée, jamais sa collecte.

import type { NetworkEntry } from "./network-ring";

/** Content-type dédié : distingue un blob de diagnostic d'un média et d'une
 *  session rrweb. C'est ce marqueur que lisent la galerie, l'indexation de
 *  rétention et la résolution du blob côté Convex. */
export const DIAGNOSTICS_CONTENT_TYPE = "application/vnd.leesto.diagnostics+json";

/** True si ce média est un blob de diagnostic (donc à NE PAS rendre comme une
 *  image ou une vidéo — il n'a pas de représentation visuelle). */
export function isDiagnosticsContentType(contentType: string | undefined | null): boolean {
  return contentType === DIAGNOSTICS_CONTENT_TYPE;
}

// --- Armement par l'URL ------------------------------------------------------
// `connectLeestoApp` s'exécute dans l'app du client et ne connaît pas le
// consentement (détenu par le shell). Un armement par message bridge laisserait
// une fenêtre de boot : les patches ne seraient posés qu'APRÈS le démarrage de
// l'app, perdant l'échec de mount et les appels API initiaux — la classe de bug
// la plus coûteuse en test. Le paramètre est lisible SYNCHRONEMENT à l'appel.
//
// Query param et non fragment : un fragment casse les apps à hash-router (échec
// dur, l'app charge la mauvaise route) là où un paramètre inconnu est toléré
// par à peu près tous les routeurs.

/** Nom du paramètre d'armement. Ne porte aucun identifiant : c'est un booléen. */
export const LEESTO_DIAG_PARAM = "leesto_diag";
/** Identifiant opaque d'une session de test. Il ne contient jamais un ticket ou
 * une donnée métier et sert uniquement à corréler les logs navigateur/backend. */
export const LEESTO_RUN_PARAM = "leesto_run";
export const LEESTO_RUN_HEADER = "X-Leesto-Run-Id";

const RUN_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isDiagnosticRunId(value: unknown): value is string {
  return typeof value === "string" && RUN_ID_RE.test(value);
}

export function createDiagnosticRunId(): string | undefined {
  try {
    const cryptoApi = globalThis.crypto;
    const uuid = cryptoApi?.randomUUID?.();
    if (isDiagnosticRunId(uuid)) return uuid;
    if (!cryptoApi?.getRandomValues) return undefined;
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    const fallback = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    return isDiagnosticRunId(fallback) ? fallback : undefined;
  } catch {
    return undefined;
  }
}

/** Ajoute le marqueur d'armement à l'URL de l'app (côté shell, seulement quand
 *  le consentement est donné). Retourne l'URL inchangée si elle est illisible —
 *  on ne casse jamais le chargement de l'iframe pour du diagnostic. */
export function withDiagnosticsParam(appUrl: string, runId?: string): string {
  try {
    const url = new URL(appUrl);
    url.searchParams.set(LEESTO_DIAG_PARAM, "1");
    if (isDiagnosticRunId(runId)) url.searchParams.set(LEESTO_RUN_PARAM, runId);
    return url.href;
  } catch {
    return appUrl;
  }
}

export function diagnosticRunIdFromSearch(search: string): string | undefined {
  try {
    const value = new URLSearchParams(search).get(LEESTO_RUN_PARAM);
    return isDiagnosticRunId(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** True si l'app a été chargée avec le marqueur d'armement (côté app). Accepte
 *  une query string brute (`location.search`) pour rester testable sans DOM. */
export function hasDiagnosticsParam(search: string): boolean {
  try {
    return new URLSearchParams(search).get(LEESTO_DIAG_PARAM) === "1";
  } catch {
    return false;
  }
}

/** Retire le marqueur d'une URL avant de la poser sur le ticket : c'est un
 *  détail d'implémentation, il n'a rien à faire dans le contexte d'un rapport. */
export function stripDiagnosticsParam(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    if (!url.searchParams.has(LEESTO_DIAG_PARAM) && !url.searchParams.has(LEESTO_RUN_PARAM))
      return rawUrl;
    url.searchParams.delete(LEESTO_DIAG_PARAM);
    url.searchParams.delete(LEESTO_RUN_PARAM);
    return url.href;
  } catch {
    return rawUrl;
  }
}

// --- Forme de la donnée ------------------------------------------------------

/** Erreur bufferisée : `console.error`/`warn`, erreur non gérée, promesse
 *  rejetée. La stack est souvent LA donnée manquante (message = symptôme). */
export type ErrorRingEntry = {
  level: "error" | "warn";
  message: string;
  stack?: string;
  mechanism?:
    | "console"
    | "window.error"
    | "unhandledrejection"
    | "resource"
    | "report"
    | "manual"
    | string;
  name?: string;
  sourceUrl?: string;
  line?: number;
  column?: number;
  at: number;
};

/** Ligne `console.log`/`info` bufferisée. */
export type ConsoleRingEntry = {
  level: "log" | "info";
  message: string;
  at: number;
};

/** Copie des trois rings à un instant t. */
export type DiagnosticsSnapshot = {
  errors: ErrorRingEntry[];
  logs: ConsoleRingEntry[];
  network: NetworkEntry[];
  /** Opaque run correlation id, present only for an armed shell session. */
  runId?: string;
};

/** True si le snapshot ne contient rien — inutile de produire un blob vide. */
export function isEmptySnapshot(s: DiagnosticsSnapshot): boolean {
  return s.errors.length === 0 && s.logs.length === 0 && s.network.length === 0;
}

// Tags des custom events maison, lus par `lib/rrwebTrace.ts` côté Convex.
// Partagés avec `diagnostics-capture.ts` : une seule définition, sinon le
// contrat SDK ↔ backend se met à diverger en silence.
export const CONSOLE_TAG = "leesto/console";
export const NETWORK_TAG = "leesto/network";
export const NETWORK_RING_TAG = "leesto/network-ring";
export const CONSOLE_RING_TAG = "leesto/console-ring";
export const DIAGNOSTICS_META_TAG = "leesto/diagnostics-meta";

/** Un custom event rrweb (`EventType.Custom`). */
const EVENT_CUSTOM = 5;

/**
 * Convertit un snapshot en tableau d'events rrweb `type: 5`.
 *
 * C'est LE point de contrat : `extractConsoleEntries` / `extractNetworkEntries`
 * (`apps/convex/lib/rrwebTrace.ts`) sont des fonctions pures sur `type: 5` et
 * fonctionnent donc sur ce tableau sans modification — les onglets Console et
 * Réseau de l'admin s'alimentent du blob de diagnostic exactement comme d'une
 * session. Un test de round-trip garde ce contrat honnête.
 */
export function diagnosticsToRrwebEvents(snapshot: DiagnosticsSnapshot): unknown[] {
  const events: unknown[] = [];

  if (snapshot.runId) {
    events.push({
      type: EVENT_CUSTOM,
      timestamp: Date.now(),
      data: { tag: DIAGNOSTICS_META_TAG, payload: { runId: snapshot.runId } },
    });
  }

  // Une erreur = un event, horodaté par l'event lui-même (extractConsoleEntries
  // lit `e.timestamp` pour ce tag).
  for (const e of snapshot.errors) {
    events.push({
      type: EVENT_CUSTOM,
      timestamp: e.at,
      data: {
        tag: CONSOLE_TAG,
        payload: {
          level: e.level,
          message: e.message,
          ...(e.stack ? { stack: e.stack } : {}),
          ...(e.mechanism ? { mechanism: e.mechanism } : {}),
          ...(e.name ? { name: e.name } : {}),
          ...(e.sourceUrl ? { sourceUrl: e.sourceUrl } : {}),
          ...(typeof e.line === "number" ? { line: e.line } : {}),
          ...(typeof e.column === "number" ? { column: e.column } : {}),
        },
      },
    });
  }

  // Les rings partent en un event chacun ; leurs entrées portent leur propre
  // `at` (log/info) ou `startedAt` (réseau).
  if (snapshot.logs.length > 0) {
    events.push({
      type: EVENT_CUSTOM,
      timestamp: snapshot.logs[snapshot.logs.length - 1]?.at ?? Date.now(),
      data: { tag: CONSOLE_RING_TAG, payload: { entries: snapshot.logs } },
    });
  }
  if (snapshot.network.length > 0) {
    events.push({
      type: EVENT_CUSTOM,
      timestamp: snapshot.network[snapshot.network.length - 1]?.startedAt ?? Date.now(),
      data: { tag: NETWORK_RING_TAG, payload: { entries: snapshot.network } },
    });
  }

  return events;
}

/** Sérialise un snapshot en Blob typé, prêt pour `uploadMedia`. */
export function serializeDiagnostics(snapshot: DiagnosticsSnapshot): Blob {
  return new Blob([JSON.stringify(diagnosticsToRrwebEvents(snapshot))], {
    type: DIAGNOSTICS_CONTENT_TYPE,
  });
}
